/*
 * Store adapter for the agent tools: the `ctx` object the executors receive.
 * Tests pass plain fixtures with the same shape instead.
 */

import tcLib from "@cclib/lib/timecode.js";
import { projectState } from "@app/store/projectStore.svelte.js";
import { eventGroupState } from "@app/store/eventGroupStore.svelte.js";
import { playerState, updateTime } from "@app/store/playerStore.svelte.js";
import { metricsState } from "@app/store/metricsStore.svelte.js";
import { editorState } from "@app/store/editorStore.svelte.js";
import { tagState } from "@app/store/tagStore.svelte.js";
import { canEditMetadata, isGroupLockedExternally } from "@app/store/permissionStore.svelte.js";
import { commitHistory } from "@app/store/historyStore.svelte.js";
import { addAgentLock } from "./agentEditLock.svelte.js";
import * as m from "@app/paraglide/messages.js";
import { getLocale } from "@app/paraglide/runtime.js";

/**
 * @param {{ turnId: string, historyName: string }} turn
 */
export function createStoreContext({ turnId, historyName }) {
    return {
        getGroups: () => eventGroupState,
        getProject: () => projectState,
        getPlayerTime: () => playerState.time,
        formatTc: (seconds) =>
            tcLib.secToTc(seconds + (projectState.incode || 0), projectState.frameRate, projectState.dropFrame),
        // The real lock state: the agent's own edit lock must not block the agent.
        isLocked: (index) => {
            const group = eventGroupState[index];
            return Boolean(group?.locked) || isGroupLockedExternally(group);
        },
        canEditMetadata,
        uiLanguage: getLocale(),
        excludeSpaces: metricsState.cpsIncludeSpaces === false,
        minFrameGap: editorState.minFrameGap || 0,
        failureNote: m.auto_correct_note(),
        getTagLibrary: () => tagState.sets.flatMap((set) => set?.tags ?? []),
        lockGroup: addAgentLock,
        // Commit by group id: the planned index is only a hint once time has passed.
        commit: (plan) => {
            if (plan.create) {
                // A new group is appended, inside the AI turn's single undo step.
                return commitHistory({
                    name: historyName,
                    eventGroup: eventGroupState.length,
                    turnId,
                    mutate: {
                        eventGroupState: (draft) => {
                            draft.push(plan.next);
                        },
                    },
                });
            }
            const groupIndex = eventGroupState.findIndex((group) => group.id === plan.groupId);
            if (groupIndex === -1) {
                throw new Error(`the event group "${plan.groupName}" no longer exists`);
            }
            return commitHistory({
                name: historyName,
                eventGroup: groupIndex,
                turnId,
                mutate: {
                    eventGroupState: (draft) => {
                        draft[groupIndex] = plan.next;
                    },
                },
            });
        },
        seek: (seconds) => {
            if (window.player) {
                window.player.currentTime = seconds;
            } else {
                updateTime(seconds);
            }
        },
        selectGroup: (index) => {
            projectState.selected = index;
        },
        selectEvents: (groupIndex, indexes) => {
            projectState.selected = groupIndex;
            eventGroupState[groupIndex].selected = indexes;
        },
    };
}
